import { useQuery } from '@tanstack/react-query';
import { LoadingState, Switch, cn } from '@v2/design-system';
import api from '@v2/lib/api';
import { useI18n } from '@v2/providers/I18nProvider';
import type { DealFieldSection, DealFieldSectionsPayload } from '../types';
import { formatBrl, formatDate, formatDateTime } from '../utils';
import DetailCard from './DetailCard';
import PaymentMethodBadge, { normalizePaymentMethodKind } from '../PaymentMethodBadge';

type Props = {
    dealId: number;
};

function toBool(value: unknown): boolean {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value !== 0;
    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        return ['1', 'true', 'sim', 'yes', 's'].includes(normalized);
    }
    return Boolean(value);
}

function formatFieldValue(value: unknown, valueType: string): string | null {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    if (valueType === 'bool') {
        return toBool(value) ? 'Sim' : 'Não';
    }

    if (valueType === 'datetime' && typeof value === 'string') {
        return formatDateTime(value) || formatDate(value);
    }

    if (valueType === 'money' && (typeof value === 'number' || typeof value === 'string')) {
        const n = Number(value);
        return Number.isNaN(n) ? String(value) : formatBrl(n);
    }

    if (valueType === 'percent' && (typeof value === 'number' || typeof value === 'string')) {
        const n = Number(value);
        if (Number.isNaN(n)) return String(value);
        // Coluna local já vem em pontos percentuais (ex.: 30); props às vezes em fração (0.3).
        const display = Math.abs(n) <= 1 ? n * 100 : n;
        return `${display.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`;
    }

    if (valueType === 'decimal' && (typeof value === 'number' || typeof value === 'string')) {
        const n = Number(value);
        if (!Number.isNaN(n) && Math.abs(n) <= 1) {
            return `${(n * 100).toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`;
        }
        if (!Number.isNaN(n) && Math.abs(n) > 100) {
            return formatBrl(n);
        }
        return String(value);
    }

    if (typeof value === 'object' && value !== null && 'name' in value) {
        const name = (value as { name?: string | null }).name;
        return name?.trim() || null;
    }

    return String(value);
}

export function FieldSectionCard({ section }: { section: DealFieldSection }) {
    const { t } = useI18n();
    const title = t(section.title_key) || section.title_key;

    return (
        <DetailCard title={title} className="shadow-none [&>div:last-child]:p-3">
            <dl className="grid gap-2 [grid-template-columns:repeat(auto-fit,minmax(max(12.5rem,25%),1fr))]">
                {section.fields.map((field) => {
                    const isBool = field.value_type === 'bool';
                    const isPaymentMethod = field.value_type === 'payment_method';
                    const boolChecked = isBool ? toBool(field.value) : null;
                    const formatted =
                        isBool || isPaymentMethod ? null : formatFieldValue(field.value, field.value_type);
                    const empty =
                        !isBool &&
                        !isPaymentMethod &&
                        (formatted == null || formatted === '');
                    const paymentLabel =
                        isPaymentMethod && typeof field.value === 'string' ? field.value : null;

                    return (
                        <div
                            key={field.field_key ?? `${field.label}-${field.field_id}`}
                            className="flex min-w-0 flex-col gap-0.5 rounded-md border border-gray-100 bg-gray-50/70 px-2.5 py-1.5 dark:border-zinc-800 dark:bg-zinc-950/40"
                        >
                            <dt
                                className="truncate text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-zinc-500"
                                title={field.label}
                            >
                                {field.label}
                            </dt>
                            <dd className="min-w-0">
                                {isBool ? (
                                    <div className="flex items-center gap-1.5">
                                        <Switch
                                            size="sm"
                                            checked={Boolean(boolChecked)}
                                            disabled
                                            readOnly
                                            aria-label={field.label}
                                            className="[&_label]:my-0"
                                            switchClassName={cn(
                                                boolChecked
                                                    ? '!border-emerald-500 !bg-emerald-500 peer-checked/switch:!border-emerald-500 peer-checked/switch:!bg-emerald-500 peer-disabled/switch:!border-emerald-500 peer-disabled/switch:!bg-emerald-500'
                                                    : '!border-red-500 !bg-red-500 peer-disabled/switch:!border-red-500 peer-disabled/switch:!bg-red-500',
                                            )}
                                            switchKnobClassName="!bg-white shadow-sm dark:!bg-white"
                                        />
                                        <span
                                            className={cn(
                                                'text-xs font-semibold',
                                                boolChecked
                                                    ? 'text-emerald-700 dark:text-emerald-400'
                                                    : 'text-gray-500 dark:text-zinc-400',
                                            )}
                                        >
                                            {boolChecked ? 'Sim' : 'Não'}
                                        </span>
                                    </div>
                                ) : isPaymentMethod ? (
                                    <PaymentMethodBadge
                                        label={paymentLabel}
                                        kind={normalizePaymentMethodKind(paymentLabel)}
                                        size="sm"
                                    />
                                ) : (
                                    <span
                                        className={cn(
                                            'line-clamp-2 break-words text-sm leading-snug',
                                            empty
                                                ? 'text-gray-400 dark:text-zinc-600'
                                                : 'font-medium text-gray-900 dark:text-zinc-100',
                                        )}
                                        title={empty ? undefined : formatted ?? undefined}
                                    >
                                        {empty ? '—' : formatted}
                                    </span>
                                )}
                            </dd>
                        </div>
                    );
                })}
            </dl>
        </DetailCard>
    );
}

export default function PloomesDealFieldSectionsPanel({ dealId }: Props) {
    const { t } = useI18n();

    const { data, isLoading, isError } = useQuery({
        queryKey: ['ploomes-deal-field-sections', dealId],
        queryFn: async () => {
            const { data: payload } = await api.get<{ data: DealFieldSectionsPayload }>(
                `/ploomes-deals/${dealId}/field-sections`,
            );
            return payload.data;
        },
        staleTime: 60_000,
    });

    if (isLoading) {
        return <LoadingState variant="detail" />;
    }

    if (isError || !data) {
        return (
            <p className="py-6 text-sm text-gray-500 dark:text-zinc-400">
                {t('v2.ploomes_deals.fields_load_error')}
            </p>
        );
    }

    if (data.sections.length === 0) {
        return (
            <p className="py-6 text-sm text-gray-500 dark:text-zinc-400">
                {t('v2.ploomes_deals.fields_empty')}
            </p>
        );
    }

    return (
        <div className="space-y-2">
            {data.sections.map((section) => (
                <FieldSectionCard key={section.key} section={section} />
            ))}
        </div>
    );
}

export function useDealFieldSectionsExtras(dealId: number) {
    return useQuery({
        queryKey: ['ploomes-deal-field-sections', dealId],
        queryFn: async () => {
            const { data: payload } = await api.get<{ data: DealFieldSectionsPayload }>(
                `/ploomes-deals/${dealId}/field-sections`,
            );
            return payload.data;
        },
        staleTime: 60_000,
    });
}
